import ReportDateRangeFilter from '@/components/reports/ReportDateRangeFilter';
import SupplierFilter from './SupplierFilter';
import { PURCHASE_STATUSES } from './types';
import { useReportDateRange } from '@/hooks/useReportDateRange';

interface DateRangeFiltersProps {
  supplier?: string;
  supplierLabel?: string;
  onSupplierChange?: (id: string, label: string) => void;
  status?: string;
  onStatusChange?: (status: string) => void;
  invoice?: string;
  onInvoiceChange?: (invoice: string) => void;
  paymentMethod?: string;
  onPaymentMethodChange?: (method: string) => void;
  user?: string;
  onUserChange?: (user: string) => void;
  item?: string;
  onItemChange?: (item: string) => void;
  asOnDate?: string;
  onAsOnDateChange?: (date: string) => void;
  paymentMethods?: string[];
  showStatus?: boolean;
  showInvoice?: boolean;
  showPaymentMethod?: boolean;
  showUser?: boolean;
  showItem?: boolean;
  showAsOnDate?: boolean;
  dateRangeHook: ReturnType<typeof useReportDateRange>;
}

export default function PurchaseReportFilters({
  supplier,
  supplierLabel,
  onSupplierChange,
  status,
  onStatusChange,
  invoice,
  onInvoiceChange,
  paymentMethod,
  onPaymentMethodChange,
  user,
  onUserChange,
  item,
  onItemChange,
  asOnDate,
  onAsOnDateChange,
  paymentMethods = [],
  showStatus,
  showInvoice,
  showPaymentMethod,
  showUser,
  showItem,
  showAsOnDate,
  dateRangeHook,
}: DateRangeFiltersProps) {
  const {
    dateRange,
    setDateRange,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
  } = dateRangeHook;

  return (
    <div className="flex flex-wrap gap-4 items-end">
      {!showAsOnDate && (
        <ReportDateRangeFilter
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          customFrom={customFrom}
          customTo={customTo}
          onCustomFromChange={setCustomFrom}
          onCustomToChange={setCustomTo}
        />
      )}

      {showAsOnDate && onAsOnDateChange && (
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-gray-700">As On Date</label>
          <input
            type="date"
            className="input-field"
            value={asOnDate || ''}
            onChange={(e) => onAsOnDateChange(e.target.value)}
          />
        </div>
      )}

      {onSupplierChange && (
        <SupplierFilter
          value={supplier || ''}
          label={supplierLabel || ''}
          onChange={onSupplierChange}
        />
      )}

      {showStatus && onStatusChange && (
        <div className="space-y-1.5 flex-1 min-w-[160px]">
          <label className="text-sm font-medium text-gray-700">Status</label>
          <select className="input-field" value={status || ''} onChange={(e) => onStatusChange(e.target.value)}>
            {PURCHASE_STATUSES.map((s) => (
              <option key={s || 'all'} value={s}>
                {s || 'All Statuses'}
              </option>
            ))}
          </select>
        </div>
      )}

      {showInvoice && onInvoiceChange && (
        <div className="space-y-1.5 flex-1 min-w-[180px]">
          <label className="text-sm font-medium text-gray-700">Purchase Invoice</label>
          <input
            type="text"
            className="input-field"
            placeholder="Search invoice..."
            value={invoice || ''}
            onChange={(e) => onInvoiceChange(e.target.value)}
          />
        </div>
      )}

      {showItem && onItemChange && (
        <div className="space-y-1.5 flex-1 min-w-[180px]">
          <label className="text-sm font-medium text-gray-700">Item</label>
          <input
            type="text"
            className="input-field"
            placeholder="Item code or name..."
            value={item || ''}
            onChange={(e) => onItemChange(e.target.value)}
          />
        </div>
      )}

      {showPaymentMethod && onPaymentMethodChange && (
        <div className="space-y-1.5 flex-1 min-w-[160px]">
          <label className="text-sm font-medium text-gray-700">Payment Method</label>
          <select
            className="input-field"
            value={paymentMethod || ''}
            onChange={(e) => onPaymentMethodChange(e.target.value)}
          >
            <option value="">All Methods</option>
            {paymentMethods.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </div>
      )}

      {showUser && onUserChange && (
        <div className="space-y-1.5 flex-1 min-w-[160px]">
          <label className="text-sm font-medium text-gray-700">User</label>
          <input
            type="text"
            className="input-field"
            placeholder="Username..."
            value={user || ''}
            onChange={(e) => onUserChange(e.target.value)}
          />
        </div>
      )}
    </div>
  );
}
